/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { Fragment, useMemo, useRef, useState } from 'react';
import Badge from 'components/v2/Badge';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { getFormatMMDDYYYY, isDateValid } from 'common/helpers/dateUtils';
import { cn } from 'utils/ui/classes';
import { Popover, Transition } from '@headlessui/react';
import CircleButton from 'components/v2/buttons/CircleButton';
import { useTranslation } from 'react-i18next';
import { filterOptionSlugs } from './columns';
import { titleize, humanize } from 'underscore.string';
import { sluggify } from 'utils/localization/utils';
const SHIPPER_FILTER_NAME = 'shipper_name';
export default function FilterChips({ columnDefs, filterModel, className, onRemove, view }) {
    var _a;
    const [open, setOpen] = useState(false);
    const { t } = useTranslation();
    const container = useRef(null);
    const showExpandButton = ((_a = container.current) === null || _a === void 0 ? void 0 : _a.scrollHeight) > 20;
    const bodyClassNames = cn('flex flex-wrap gap-4 mb-2', {
        'h-5 overflow-hidden': !open,
    });
    const setFieldName = (acc, column) => {
        if (column.children) {
            column.children.reduce(setFieldName, acc);
        }
        acc[column.field] = column.headerName;
        return acc;
    };
    const filterNames = useMemo(() => (columnDefs === null || columnDefs === void 0 ? void 0 : columnDefs.reduce(setFieldName, {})) || [], [view]);
    if (!filterModel || !Object.keys(filterModel).length)
        return null;
    const filters = Object.keys(filterModel)
        .filter((name) => Object.keys(filterModel[name]).length) // exclude empty filters
        .map((name) => (Object.assign({ name }, filterModel[name])));
    const renderFilter = (data) => {
        const { filterType } = data;
        if (data.name === SHIPPER_FILTER_NAME) {
            return renderCustomerFilter(data);
        }
        switch (filterType) {
            case 'date':
                return renderDateFilter(data);
            case 'number':
                return renderNumberFilter(data);
            case 'text':
                return renderTextFilter(data);
            case 'set':
                return renderSetFilter(data);
            default:
                return renderLsvFilter(data);
        }
    };
    const renderName = (name) => {
        return filterNames[name];
    };
    const renderType = (data, numeric = false) => {
        if (numeric) {
            if (data.type === 'equals')
                return '=';
            if (data.type === 'lessThan')
                return '<';
            if (data.type === 'lessThanOrEqual')
                return '<=';
            if (data.type === 'greaterThan')
                return '>';
            if (data.type === 'greaterThanOrEqual')
                return '>=';
        }
        return data.type === 'equals' ? ':' : t(filterOptionSlugs[data.type]);
    };
    const renderDates = (data) => {
        const dateFrom = (isDateValid(data.dateFrom) && getFormatMMDDYYYY(data.dateFrom)) || '';
        const dateTo = (isDateValid(data.dateTo) && getFormatMMDDYYYY(data.dateTo)) || '';
        const formattedDateRange = [];
        if (dateFrom)
            formattedDateRange.push(dateFrom);
        if (dateTo)
            formattedDateRange.push(dateTo);
        return formattedDateRange.join(" - ");
    };
    const renderOperator = (operator) => {
        return operator || 'AND';
    };
    const renderDateFilter = (data) => {
        const getCondition = (condition) => {
            return `${renderName(data.name)} ${renderType(condition)} ${renderDates(condition)}`;
        };
        const conditions = data.conditions
            ? data.conditions.map((condition) => getCondition(condition))
            : [getCondition(data)];
        return conditions.join(` ${renderOperator(data.operator)} `);
    };
    const renderNumberFilter = (data) => {
        const getCondition = (condition) => {
            return `${renderType(condition, true)} ${condition.filter}`;
        };
        const conditions = data.conditions
            ? data.conditions.map((condition) => getCondition(condition))
            : [getCondition(data)];
        return `${renderName(data.name)} ${conditions.join(` ${renderOperator(data.operator)} `)}`;
    };
    const renderTextFilter = (data) => {
        const getCondition = (condition) => {
            return `${renderType(condition)}${condition.filter ? ' ' + condition.filter : ''}`;
        };
        const conditions = data.conditions
            ? data.conditions.map((condition) => getCondition(condition))
            : [getCondition(data)];
        return `${renderName(data.name)} ${conditions.join(` ${renderOperator(data.operator)} `)}`;
    };
    const renderCustomerFilter = (data) => {
        var _a, _b, _c, _d, _e;
        const getCondition = (condition) => {
            return `${renderType(condition)} ${condition.filter}`;
        };
        const values = ((_b = (_a = data.filterModels[0]) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 : _b.map((value) => value.name)) || [];
        const condition1 = ((_c = data.filterModels[1]) === null || _c === void 0 ? void 0 : _c.condition1)
            ? getCondition(data.filterModels[1].condition1)
            : '';
        const condition2 = ((_d = data.filterModels[1]) === null || _d === void 0 ? void 0 : _d.condition2)
            ? getCondition(data.filterModels[1].condition2)
            : '';
        const conditions = [condition1, condition2].filter((condition) => condition);
        const renderedValues = values ? `is ${values.join(` OR `)}` : '';
        const renderedConditions = `${conditions.join(` ${renderOperator((_e = data.filterModels[1]) === null || _e === void 0 ? void 0 : _e.operator)} `)}`;
        const middleOperator = renderedValues && renderedConditions ? 'OR' : '';
        return `${renderName(data.name)} ${renderedValues} ${middleOperator} ${renderedConditions}`;
    };
    const renderLsvFilter = (data) => {
        const getCondition = (condition) => {
            return `${renderType(condition)} ${condition.filter}`;
        };
        const conditions = [];
        Object.keys(data).forEach((key) => {
            if (key.includes('condition')) {
                conditions.push(data[key]);
            }
        });
        if (conditions.length === 1) {
            return `${renderName(data.name)} ${getCondition(conditions[0])}`;
        }
        return (React.createElement(React.Fragment, null,
            renderName(data.name),
            " (",
            renderPopoverList(conditions.map((condition) => condition.filter)),
            ")"));
    };
    const renderSetFilter = (data) => {
        const values = data.values.map((value) => titleize(humanize(t(sluggify(value)))));
        if (values.length === 1) {
            return `${renderName(data.name)}: ${values[0]}`;
        }
        return (React.createElement(React.Fragment, null,
            renderName(data.name),
            " (",
            renderPopoverList(values),
            ")"));
    };
    const renderPopoverList = (values) => {
        return (React.createElement(Popover, { className: "relative" },
            React.createElement(React.Fragment, null,
                React.createElement(Popover.Button, null,
                    React.createElement("span", { className: "text-blue-500 hover:text-blue-700" }, values.length)),
                React.createElement(Transition, { as: Fragment, enter: "transition ease-out duration-200", enterFrom: "opacity-0 translate-y-1", enterTo: "opacity-100 translate-y-0", leave: "transition ease-in duration-150", leaveFrom: "opacity-100 translate-y-0", leaveTo: "opacity-0 translate-y-1" },
                    React.createElement(Popover.Panel, { className: "fixed z-10 mt-3 -translate-x-1/2 transform px-4 sm:px-0" },
                        React.createElement("div", { className: "overflow-hidden rounded-lg shadow-lg ring-1 ring-black/5 w-64" },
                            React.createElement("div", { className: "relative bg-white px-4 py-2 divide-y divide-y-100 text-sm" }, values.map((value, i) => (React.createElement("p", { className: "py-2", key: i }, value))))))))));
    };
    return (React.createElement("div", { className: className },
        React.createElement("div", { className: bodyClassNames, ref: container }, filters.map((filterData) => (React.createElement(Badge, { withRemove: true, color: "blue", onRemove: () => {
                onRemove === null || onRemove === void 0 ? void 0 : onRemove(filterData.name);
            }, key: filterData.name }, renderFilter(filterData))))),
        showExpandButton && (React.createElement(CircleButton, { color: "blue", size: "small", onClick: () => setOpen(!open) },
            !open && React.createElement(ChevronDownIcon, { className: "w-5 h-5" }),
            open && React.createElement(ChevronUpIcon, { className: "w-5 h-5" })))));
}
